import { useEffect, useState } from "react";

import { API_BASE_URL, getApiHeaders } from "../../../../config";

/**
 * Collection-level data for the listings screen: the listings themselves
 * plus the units and cities the form needs, and the status quick action.
 *
 * Every request is a verbatim move out of AdminListingsPage.js (L1
 * extraction) — same URLs, same methods, same response handling, same
 * error strings — with one deliberate exception carried in from L2: the
 * units call now asks for `limit=200` instead of accepting the endpoint's
 * default of 50 (discovery risk R10), because the overview resolves each
 * row's object label out of this list and silently showed "—" past unit 50.
 * The status PATCH still merges the response without a refetch. It now also
 * hands the merged row back to its caller, which is what lets the page sync
 * an open editor's publish checkbox after a confirmed publish PATCH
 * (discovery risk R1, fixed in L6-SAFETY). A failed PATCH still resolves to
 * undefined, so the caller can tell the two apart without a second contract.
 */
export default function useListingsData() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(true);

  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [unitsError, setUnitsError] = useState("");

  const [statusUpdateLoadingId, setStatusUpdateLoadingId] = useState(null);
  const [statusUpdateError, setStatusUpdateError] = useState("");

  const fetchListings = () => {
    setLoading(true);
    setError("");
    fetch(`${API_BASE_URL}/api/admin/listings`, { headers: getApiHeaders() })
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) throw new Error("Nicht autorisiert. Bitte anmelden.");
          if (res.status === 403) throw new Error("Keine Berechtigung.");
          throw new Error("Listings konnten nicht geladen werden.");
        }
        return res.json();
      })
      .then((data) => {
        setListings(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error(err);
        setError(err.message || "Fehler beim Laden.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchListings();
  }, []);

  useEffect(() => {
    setCitiesLoading(true);
    fetch(`${API_BASE_URL}/api/admin/cities`, { headers: getApiHeaders() })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("Cities laden fehlgeschlagen"))))
      .then((data) => setCities(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Cities:", err))
      .finally(() => setCitiesLoading(false));
  }, []);

  useEffect(() => {
    setUnitsLoading(true);
    setUnitsError("");
    fetch(`${API_BASE_URL}/api/admin/units?limit=200`, { headers: getApiHeaders() })
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) throw new Error("Nicht autorisiert.");
          if (res.status === 403) throw new Error("Keine Berechtigung.");
          throw new Error("Units konnten nicht geladen werden.");
        }
        return res.json();
      })
      .then((data) => {
        const list = data && typeof data.items !== "undefined" ? data.items : (Array.isArray(data) ? data : []);
        setUnits(list);
      })
      .catch((err) => {
        console.error(err);
        setUnitsError(err.message || "Fehler beim Laden der Units.");
      })
      .finally(() => setUnitsLoading(false));
  }, []);

  const patchListingStatus = (listingId, payload) => {
    setStatusUpdateError("");
    setStatusUpdateLoadingId(listingId);
    return fetch(`${API_BASE_URL}/api/admin/listings/${encodeURIComponent(listingId)}/status`, {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().then((d) => { throw new Error(d.detail || "Aktualisierung fehlgeschlagen."); }).catch((e) => { throw e.message ? e : new Error(`Fehler ${res.status}`); });
        }
        return res.json();
      })
      .then((updated) => {
        setListings((prev) => prev.map((l) => (l.id === listingId ? { ...l, ...updated } : l)));
        return updated;
      })
      .catch((err) => setStatusUpdateError(err.message || "Aktualisierung fehlgeschlagen."))
      .finally(() => setStatusUpdateLoadingId(null));
  };


  return {
    listings,
    setListings,
    loading,
    error,
    cities,
    citiesLoading,
    units,
    unitsLoading,
    unitsError,
    statusUpdateLoadingId,
    statusUpdateError,
    fetchListings,
    patchListingStatus,
  };
}
